/**
 * Bridge between the existing calendar controller/stores and the prototype
 * MobiliCalendar engine. Consumes exactly the same data contract as the
 * FullCalendar-based Calendar so both can run side by side.
 */
import { EventReceiveArg } from "@fullcalendar/interaction";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useStore from "../../helpers/useStore";
import notificationStore from "../../stores/Notification/notificationStore";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import {
  useDeleteWorkHourException,
  usePatchContinuousTimeSlotTimesAndUser,
  usePatchTimeSlotTimesAndUser,
  useUpdateContinuousTimeSlot,
  useUpdateTimeSlot,
} from "../../stores/Calendar/calendar.mutations";
import { useCachedAppointmentLookup } from "../../stores/Calendar/calendar.queries";
import { useCalendarController } from "../Calendar/controller/CalendarController";
import { useMergeAppointmentGapsWarning } from "../Calendar/controller/shared/useMergeAppointmentGapsWarning";
import useCalendarValidations from "../Calendar/Calendar.validations";
import { eventToClientTimeSlot } from "../Calendar/controller/adapters/display-to-client/reverse.adapter";
import { useEventShelfController } from "../Calendar/EventShelf/EventShelfController";
import { useDynamicTemplateProviderController } from "../Calendar/DynamicTemplateProvider";
import {
  durationToMinutes,
  resolvePinboardItemPayload,
} from "../Calendar/hotkeys/calendarHotkeys";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { useNavigate } from "react-router-dom";
import MobiliCalendar, { MobiliCalendarKeyboardApi } from "./MobiliCalendar";
import { ActionServices, buildActionContext, runCalendarAction } from "./actions";
import CalendarContextMenu, {
  CalendarContextMenuState,
} from "./menu/CalendarContextMenu";
import { useContinuousCalendarController } from "./controller/useContinuousCalendarController";
import { DEFAULT_GRID_CONFIG } from "./model/types";
import { resolveMoveTimes } from "./model/pendingMoves";
import { buildContinuousDayViewModel } from "./viewmodel/continuousDay";
import { buildStandardDayViewModel } from "./viewmodel/standardDay";
import { MCEvent, MCEventMove, MCExternalDrop, MCStats } from "./model/types";
import {
  MobiliCalendarHotkeyDeps,
  useMobiliCalendarHotkeys,
} from "./useMobiliCalendarHotkeys";
import { locallyCheckableTherapyIds, slotTherapyList } from "../../helpers/slotTherapy";

interface MobiliCalendarWithControllerProps {
  onStats?: (stats: MCStats) => void;
}

/**
 * External payload types that must never be scheduled through the receive
 * path (mirrors the old Calendar.handleEventReceive guard).
 */
const INVALID_RECEIVE_TYPES = [
  "TimeSlot",
  "EventTimeSlot",
  "ContinuousEventTimeSlot",
  "LeaveOfAbsence",
  "TravelTime",
  "LunchBreak",
  "SimpleWorkingHours",
];

const MobiliCalendarWithController: React.FC<MobiliCalendarWithControllerProps> = observer(
  ({ onStats }) => {
    const CalendarStore = useStore(CalendarContext);
    const controller = useCalendarController();
    const { mutateAsync: rescheduleTimeSlot } = usePatchTimeSlotTimesAndUser();
    const { mutateAsync: rescheduleContinuousTimeSlot } =
      usePatchContinuousTimeSlotTimesAndUser();
    const { mutateAsync: updateTimeSlot } = useUpdateTimeSlot();
    const { mutateAsync: updateContinuousTimeSlot } = useUpdateContinuousTimeSlot();
    const { hasTherapistCredentials, frequencyValidation } = useCalendarValidations();
    // Visit-gap warning after a move; the dialog is hosted shell-wide by
    // CalendarActionDialogs (SchedulerDialogs), which MobiliScheduler mounts.
    const { showVisitGapWarningIfPresent } = useMergeAppointmentGapsWarning();

    // The controller handlers/data are consumed inside stable callbacks via a
    // ref so rapid consecutive drops never close over a stale controller.
    const controllerRef = useRef(controller);
    controllerRef.current = controller;

    const handleJumpTargetHandled = useCallback(() => {
      CalendarStore.setTargetAppointmentId(undefined);
    }, [CalendarStore]);

    /**
     * Patient-overview wiring — same contract as the old calendar:
     *  - click / Enter selects immediately via the canonical
     *    CalendarCommands.onEventContentClick (patient id + last name + rx id
     *    + drawer open, sales flag handling included);
     *  - keyboard cursoring runs the SAME command 500 ms debounced, so
     *    arrowing across appointments doesn't thrash the drawer.
     * MCEvent.raw is the adapter CalendarEvent, so the existing reverse
     * adapter bridges without any new conversion code.
     */
    const openPatientOverview = useCallback(
      (event: MCEvent) => {
        const clientTimeSlot = eventToClientTimeSlot(event.raw);
        void CalendarCommands(CalendarStore).onEventContentClick(clientTimeSlot);
      },
      [CalendarStore],
    );

    const handleEventSelect = useCallback(
      (event: MCEvent) => {
        if (event.patientId != null) openPatientOverview(event);
      },
      [openPatientOverview],
    );

    const debouncedCursorOverview = useMemo(
      () =>
        debounce((event: MCEvent | null) => {
          if (event?.patientId != null) openPatientOverview(event);
        }, 500),
      [openPatientOverview],
    );
    useEffect(
      () => () => debouncedCursorOverview.cancel(),
      [debouncedCursorOverview],
    );

    /**
     * Persist a move (drag / keyboard "verschieben" / grouping bar) with the
     * same verbs as the old calendar's drag&drop: appointment slots →
     * scheduler RESCHEDULE (SPLIT/MERGE semantics intact), plain time slots
     * (GoneFishing/EventTimeSlot/TravelTime…) → updateTimeSlot. Every mutation
     * carries its own optimistic cache patch + rollback and a per-slot
     * sequence guard, so rapid successive moves of the same slot stay
     * last-issued-wins.
     *
     * Returns a promise that settles when ALL dispatched mutations have — the
     * engine uses it to retire its visual continuity patches (an error
     * rollback then snaps the event back exactly when the cache is restored).
     */
    const handleMoveEvents = useCallback(
      async (moves: MCEventMove[]) => {
        const tasks: Promise<unknown>[] = [];
        for (const move of moves) {
          const raw = move.event.raw;
          const slotId = raw.extendedProps?.timeSlotId ?? String(raw.id ?? "");
          const times = resolveMoveTimes(raw, move.targetStartMin);
          if (!slotId || !times) continue;

          // Therapist-credential gate, mirroring the old handleEventChange —
          // only when the therapist actually changes (a same-column time move
          // cannot lose credentials; the lookup is react-query cached for 1h,
          // so cross-column checks are effectively instant after the first).
          //
          // MD-232: a slot backed by a prescription line (`therapyRxId`) is NOT
          // gated on the line item it currently carries. That item was resolved
          // for the OLD therapist — a KG-ZNS slot on a Bobath therapist carries
          // `GKV_KGN BO` — and testing the new therapist against it rejects
          // every cross-credential move, even though the Rx's prescribed TERM
          // resolves perfectly well to that therapist's own variant (PNF).
          // Only the backend knows the term's candidate set, so the move goes
          // through and `rescheduleTimeSlot` re-resolves; a therapist
          // credentialed for none of the candidates comes back as a 409 whose
          // message this same notification surfaces, and the optimistic patch
          // rolls back. Slots with no prescription line (nothing to re-resolve
          // from) keep the local gate.
          if (
            move.event.eventType === "appointment" &&
            move.targetResourceId !== move.event.resourceId
          ) {
            const therapy = raw.extendedProps?.therapy;
            const hasPrescriptionLine = Boolean(raw.extendedProps?.therapyRxId);
            if (therapy?.id && !hasPrescriptionLine) {
              const allowed = await hasTherapistCredentials(move.targetResourceId, therapy.id);
              if (!allowed) {
                notificationStore.showMessage(
                  `Therapeut hat kein Heilmittel für diese Therapie: ${therapy.abbreviation ?? ""}`,
                  "warning",
                );
                continue; // engine retires this event's continuity patch on settle
              }
            }
          }

          const { start, end } = times;
          if (move.event.eventType === "appointment") {
            const mutate = move.event.isContinuous
              ? rescheduleContinuousTimeSlot
              : rescheduleTimeSlot;
            tasks.push(
              mutate({
                id: slotId,
                start: start.toISOString(),
                end: end.toISOString(),
                userId: move.targetResourceId,
              }),
            );
          } else {
            const timeslot = {
              id: slotId,
              start: start.toISOString(),
              end: end.toISOString(),
              type: move.event.timeSlotType,
              user: { id: move.targetResourceId },
            };
            tasks.push(
              move.event.isContinuous
                ? updateContinuousTimeSlot({ id: slotId, timeslot })
                : updateTimeSlot({ id: slotId, timeslot }),
            );
          }
        }
        // Rollback + error toasts are handled inside each mutation; here we
        // only wait for settlement so the engine can retire its patches.
        const settled = await Promise.allSettled(tasks);

        // Moving ONE treatment of a multi-slot visit can leave the patient
        // waiting between their treatments. Each mutation answers with the
        // survivor appointment, so the gap is read straight off the result;
        // only the first is reported, since a multi-slot drag lands them all in
        // the same visit and would otherwise raise the same dialog N times.
        const survivor = settled.find(
          (outcome): outcome is PromiseFulfilledResult<any> => outcome.status === "fulfilled",
        )?.value;
        showVisitGapWarningIfPresent(survivor);
      },
      [
        hasTherapistCredentials,
        showVisitGapWarningIfPresent,
        rescheduleTimeSlot,
        rescheduleContinuousTimeSlot,
        updateTimeSlot,
        updateContinuousTimeSlot,
      ],
    );

    /**
     * External drop receiver (shelf cards & slot grips, pinboard pins,
     * cancellation-drawer cards, dynamic-template chips).
     *
     * Replicates the old Calendar.handleEventReceive pre-checks (therapist
     * active, payload type, frequency, credentials), then hands the payload to
     * the controller's existing onEventReceive through a minimal
     * EventReceiveArg-compatible shim — so templates (incl. the
     * TemplateScheduleDialog path), single-slot verbs, pinboard cleanup,
     * optimistic whole-appointment saves and continuous mode all reuse the
     * exact same battle-tested controller code with zero duplication.
     */
    const handleExternalDrop = useCallback(
      async (drop: MCExternalDrop): Promise<boolean> => {
        const { payload, resourceId, start, end } = drop;
        const data = controllerRef.current?.data;
        const handlers = controllerRef.current?.handlers;
        if (!data || !handlers) return false;

        // Therapist must be active on the target date (old canCreateAppointmentForDate).
        const user = data.users?.find((u) => u.id === resourceId);
        if (!user) return false;
        if (user.deletedAt) {
          notificationStore.showMessage(
            "Termine können nicht für deaktivierte Therapeuten erstellt werden.",
            "warning",
          );
          return false;
        }
        if (user.lastWorkDay && moment(start).isAfter(user.lastWorkDay)) {
          notificationStore.showMessage(
            `Termine können nicht nach dem letzten Arbeitstag (${moment(user.lastWorkDay).format("L")}) erstellt werden.`,
            "warning",
          );
          return false;
        }

        // Plain time-slot payloads are never schedulable via receive.
        const payloadType =
          (payload as { type?: string }).type ??
          (payload as { timeSlotType?: string }).timeSlotType;
        if (payloadType && INVALID_RECEIVE_TYPES.includes(payloadType)) return false;

        const isTemplate = Boolean(payload.isTemplate);
        try {
          // Frequency limit (skipped for templates and in continuous view,
          // like the old calendar).
          if (!isTemplate && !data.isContinuousView) {
            const frequency = await frequencyValidation(
              payload.id,
              moment(start).format("YYYY-MM-DD"),
            );
            if (frequency && frequency.validity === "Not Valid") {
              notificationStore.showMessage(frequency.message, "warning");
              return false;
            }
          }

          // Tarif-bound therapy credentials for every payload slot the client
          // can judge on its own — i.e. not the prescription-backed ones, whose
          // line item belongs to the therapist they were last scheduled with
          // and is re-resolved server-side on drop (see
          // locallyCheckableTherapyIds).
          const therapyIds = locallyCheckableTherapyIds(payload.timeSlots);
          if (therapyIds.length > 0) {
            const allowed = await hasTherapistCredentials(resourceId, therapyIds);
            if (!allowed) {
              const abbreviations = slotTherapyList(payload.timeSlots);
              notificationStore.showMessage(
                `Therapeut hat kein Heilmittel für diese Therapie: ${abbreviations}`,
                "warning",
              );
              return false;
            }
          }

          // Minimal EventReceiveArg shim — only the members the controller's
          // onEventReceive actually reads. No FullCalendar instance involved.
          const shim = {
            event: {
              id: payload.id,
              start,
              end,
              extendedProps: payload,
              getResources: () => [{ id: resourceId }],
              remove: () => {},
            },
            revert: () => {},
          } as unknown as EventReceiveArg;

          await handlers.onEventReceive(shim);
          return true;
        } catch (error) {
          console.error("Error receiving external drop:", error);
          return false;
        }
      },
      [frequencyValidation, hasTherapistCredentials],
    );

    // ── Keyboard contract wiring (keyboardSpec.ts) ─────────────────────────
    // Shelf + template snapshots for the shared b/a logic (same hooks the
    // shell's EventShelf uses — react-query caches make the second consumer
    // free), the engine cursor api, and the pinboard place step for "v".
    const eventShelfController = useEventShelfController();
    const eventShelfControllerRef = useRef(eventShelfController);
    eventShelfControllerRef.current = eventShelfController;
    const templateProviderController = useDynamicTemplateProviderController();
    const templateProviderControllerRef = useRef(templateProviderController);
    templateProviderControllerRef.current = templateProviderController;
    const patientsStore = useStore(PatientsContext);
    const navigate = useNavigate();
    const { navigateToPatient } = PatientCommands(patientsStore);
    const keyboardApiRef = useRef<MobiliCalendarKeyboardApi | null>(null);

    /**
     * "v" on an empty cell — umziehen place step (spec: umziehen): plant the
     * most recently pinned Ablage item (LIFO) at the cell through the same
     * validated drop pipeline; the controller unpins it on success.
     */
    const handleEmptyCellV = useCallback(
      (cell: { resourceId: string; slotMin: number }): boolean => {
        const pins = CalendarStore.pinBoardItems;
        if (!pins || pins.length === 0) return false;
        const resolved = resolvePinboardItemPayload(pins[pins.length - 1]);
        if (!resolved) return false;
        const minutes = durationToMinutes(resolved.payload.duration);
        if (!minutes) return false;
        const hh = String(Math.floor(cell.slotMin / 60)).padStart(2, "0");
        const mm = String(cell.slotMin % 60).padStart(2, "0");
        const start = new Date(`${CalendarStore.selectedDateStr}T${hh}:${mm}:00`);
        const end = new Date(start.getTime() + minutes * 60_000);
        void handleExternalDrop({
          payload: resolved.payload as MCExternalDrop["payload"],
          resourceId: cell.resourceId,
          start,
          end,
        });
        return true;
      },
      [CalendarStore, handleExternalDrop],
    );

    const hotkeyDepsRef = useRef<MobiliCalendarHotkeyDeps>(
      undefined as unknown as MobiliCalendarHotkeyDeps,
    );
    hotkeyDepsRef.current = {
      calendarStore: CalendarStore,
      api: () => keyboardApiRef.current,
      getShelfData: () => eventShelfControllerRef.current?.allShelvesData ?? [],
      getTemplateData: () => ({
        visible: templateProviderControllerRef.current.isVisible,
        handles: templateProviderControllerRef.current.handles,
      }),
      receiveExternalDrop: handleExternalDrop,
      navigateToPatient: (patientId, rxId, subtab) =>
        navigateToPatient(navigate, patientId, rxId!, subtab),
      runAction: (actionId, event) =>
        runCalendarAction(actionId, event, actionServicesRef.current),
    };
    // ── Context menu (action registry) ─────────────────────────────────────
    // The engine reports (event, position); everything else — context build,
    // registry filtering, dialogs — happens here in the shell.
    const { lookupByAppointmentId } = useCachedAppointmentLookup();
    const { mutateAsync: deleteWorkHourExceptionMutation } = useDeleteWorkHourException();
    const [contextMenu, setContextMenu] = useState<CalendarContextMenuState | null>(null);

    const actionServicesRef = useRef<ActionServices>(undefined as unknown as ActionServices);
    actionServicesRef.current = {
      calendarStore: CalendarStore,
      controller: () => controllerRef.current?.handlers ?? null,
      lookupByAppointmentId,
      navigateToPatient: (patientId, rxId, subtab) =>
        navigateToPatient(navigate, patientId, rxId!, subtab),
      deleteWorkHourException: (exceptionId) =>
        deleteWorkHourExceptionMutation({ exceptionId }),
      notify: (message, level) => notificationStore.showMessage(message, level),
      openDialog: CalendarStore.openCalendarDialog,
    };

    const handleEventContextMenu = useCallback(
      (event: MCEvent, position: { x: number; y: number }) => {
        const ctx = buildActionContext(event);
        if (!ctx) return;
        setContextMenu({ ctx, x: position.x, y: position.y });
      },
      [],
    );
    const closeContextMenu = useCallback(() => setContextMenu(null), []);

    useMobiliCalendarHotkeys(hotkeyDepsRef);

    // View-model selection: one builder per mode, never branches inside one.
    // Standard day consumes the legacy mixed controller's data; continuous
    // consumes the NEW data-only continuous controller. Mutation flows stay
    // shared: moves pick their verb per event (`isContinuous`), and external
    // drops go through the legacy controller's continuous-aware
    // onEventReceive either way.
    const isContinuousView = CalendarStore.continuousView;
    const continuousData = useContinuousCalendarController(isContinuousView);
    const controllerData = controller?.data;
    const viewModel = useMemo(() => {
      if (isContinuousView) {
        return buildContinuousDayViewModel({
          resources: continuousData.resources,
          events: continuousData.calendarEvents,
          date: continuousData.selectedDate,
          patientAvailabilities: continuousData.patientAvailabilities,
          cfg: DEFAULT_GRID_CONFIG,
        });
      }
      return controllerData
        ? buildStandardDayViewModel({
            resources: controllerData.resources,
            events: controllerData.calendarEvents,
            date: controllerData.selectedDate,
            patientAvailabilities: controllerData.patientAvailabilities,
            patientUnavailableTypes: controllerData.patientUnavailableTypes,
            cfg: DEFAULT_GRID_CONFIG,
          })
        : null;
    }, [isContinuousView, continuousData, controllerData]);

    if (!controller || !viewModel) return null;
    const { data } = controller;

    return (
      <>
      <MobiliCalendar
        events={viewModel.events}
        resources={viewModel.resources}
        patientBlockedRanges={viewModel.patientBlockedRanges}
        date={isContinuousView ? continuousData.selectedDate : data.selectedDate}
        // A weekly template has no calendar week — show the weekday instead.
        cornerLabel={
          isContinuousView ? moment(continuousData.selectedDate).format("ddd") : undefined
        }
        isLoading={isContinuousView ? continuousData.isLoading : data.isLoading}
        selectedPatientId={CalendarStore.selectedPatientId ?? null}
        targetAppointmentId={CalendarStore.targetAppointmentId ?? null}
        onJumpTargetHandled={handleJumpTargetHandled}
        onEventSelect={handleEventSelect}
        onEventCursor={debouncedCursorOverview}
        onMoveEvents={handleMoveEvents}
        onExternalDrop={handleExternalDrop}
        onEmptyCellV={handleEmptyCellV}
        apiRef={keyboardApiRef}
        onStats={onStats}
        onEventContextMenu={handleEventContextMenu}
      />
      {contextMenu && (
        <CalendarContextMenu
          state={contextMenu}
          services={actionServicesRef.current}
          onClose={closeContextMenu}
        />
      )}
      </>
    );
  },
);

export default MobiliCalendarWithController;
